import { useEffect, useMemo, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { num2 } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SearchSelect from "@/components/SearchSelect";

export default function CustomerPrices({ customerId }) {
  const { can } = useAuth();
  const editable = can("vouchers");
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [draft, setDraft] = useState({ product_id: "", price: "" });
  const [edits, setEdits] = useState({});
  const productMap = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

  const load = () => api.get(`/customer-prices?customer_id=${customerId}`).then(r => setPrices(r.data));
  useEffect(() => { api.get("/products").then(r => setProducts(r.data)); load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (product_id, price) => {
    if (!product_id) return toast.error("Choose a product");
    if (price === "" || Number(price) < 0) return toast.error("Enter a valid price");
    try {
      await api.post("/customer-prices", { customer_id: customerId, product_id, price: Number(price) });
      toast.success("Custom price saved");
      setDraft({ product_id: "", price: "" }); setEdits(e => { const n = { ...e }; delete n[product_id]; return n; }); load();
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };
  const remove = async (product_id) => {
    await api.delete("/customer-prices", { params: { customer_id: customerId, product_id } });
    toast.success("Custom price removed"); load();
  };

  const available = products.filter(p => p.active !== false && !prices.some(x => x.product_id === p.id));
  const sorted = [...prices].sort((a, b) => (productMap[a.product_id]?.name || "").localeCompare(productMap[b.product_id]?.name || ""));

  return (
    <div className="bg-white border border-slate-200 rounded-md table-scroll" data-testid="customer-prices-section">
      <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider">Custom Prices</div>
        <div className="text-[11px] text-slate-500">Used as the default rate on invoices for this customer · still editable per line</div>
      </div>
      <table className="acct-table">
        <thead><tr><th>Product</th><th style={{ width: "90px" }}>Unit</th><th style={{ width: "120px" }} className="num">Default ₹</th><th style={{ width: "150px" }} className="num">Custom ₹</th><th style={{ width: "80px" }}></th></tr></thead>
        <tbody>
          {sorted.map(x => {
            const p = productMap[x.product_id];
            const val = edits[x.product_id] ?? x.price;
            const dirty = edits[x.product_id] !== undefined && Number(edits[x.product_id]) !== Number(x.price);
            return (
              <tr key={x.product_id} data-testid={`custom-price-row-${x.product_id}`}>
                <td className="font-medium">{p?.name || x.product_id}</td>
                <td className="mono text-slate-500">{p?.unit}</td>
                <td className="num mono text-slate-500">{num2(p?.rate)}</td>
                <td><input data-testid={`custom-price-input-${x.product_id}`} className="cell-input num" type="number" step="0.01" value={val} readOnly={!editable}
                  onChange={e => setEdits(s => ({ ...s, [x.product_id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") save(x.product_id, val); }} /></td>
                <td className="text-right whitespace-nowrap">
                  {dirty && <button data-testid={`custom-price-save-${x.product_id}`} onClick={() => save(x.product_id, val)} className="p-1 text-emerald-700 hover:text-emerald-900" title="Save"><Check size={14} /></button>}
                  {editable && <button data-testid={`custom-price-delete-${x.product_id}`} onClick={() => remove(x.product_id)} className="p-1 text-slate-400 hover:text-rose-700" title="Remove"><Trash2 size={13} /></button>}
                </td>
              </tr>
            );
          })}
          {!sorted.length && <tr><td colSpan="5" className="text-center text-xs text-slate-400 py-3" data-testid="custom-prices-empty">No custom prices yet — product default rates apply.</td></tr>}
          {editable && <tr className="bg-slate-50" data-testid="custom-price-new-row">
            <td style={{ overflow: "visible" }}>
              <SearchSelect testid="custom-price-product" placeholder="Search product to add…" value={draft.product_id}
                options={available.map(p => ({ value: p.id, label: p.name, sub: `${p.unit} · ₹${num2(p.rate)}`, keywords: [p.unit] }))}
                onChange={(v) => { setDraft(d => ({ ...d, product_id: v, price: productMap[v] ? productMap[v].rate : "" })); if (v) setTimeout(() => document.querySelector("[data-testid='custom-price-value']")?.select(), 30); }} />
            </td>
            <td className="mono text-slate-500">{productMap[draft.product_id]?.unit}</td>
            <td className="num mono text-slate-500">{draft.product_id ? num2(productMap[draft.product_id]?.rate) : ""}</td>
            <td><input data-testid="custom-price-value" className="cell-input num" type="number" step="0.01" placeholder="0.00" value={draft.price}
              onChange={e => setDraft(d => ({ ...d, price: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") save(draft.product_id, draft.price); }} /></td>
            <td className="text-right"><button data-testid="custom-price-add-btn" onClick={() => save(draft.product_id, draft.price)} className="h-7 px-2 bg-slate-900 hover:bg-slate-800 text-white rounded text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1"><Plus size={12} />Add</button></td>
          </tr>}
        </tbody>
      </table>
    </div>
  );
}
